/**
 * Floating Toolbar
 *
 * Coordinates positioning of fixed-bottom-right floating action buttons.
 * Each button registers at a position (1-based) and the toolbar calculates
 * the `right` CSS offset so buttons line up without overlapping.
 *
 * Uses a window singleton so separate bundles (shop, share, etc.) can
 * register independently regardless of load order.
 *
 * Layout:  lowest order is rightmost, next order is to the left, etc.
 *   right = EDGE + index * (SLOT + GAP)
 *   where index is the item's position in the order-sorted list.
 */

export const SLOT = 50;  // width reserved per button
const GAP  = 10;  // space between slots
const EDGE = 20;  // distance from viewport right edge

function getToolbar() {
  if (!window.__floatingToolbar) {
    window.__floatingToolbar = { items: [] };
  }
  return window.__floatingToolbar;
}

function reflow() {
  const toolbar = getToolbar();
  const sorted = [...toolbar.items].sort((a, b) => a.order - b.order);
  sorted.forEach((item, index) => {
    item.el.style.right = EDGE + index * (SLOT + GAP) + 'px';
  });
}

/**
 * Register a floating button in the toolbar.
 *
 * @param {HTMLElement} el    - The button/anchor element (must already be position:fixed).
 * @param {number}      order - Sort order (lower = further right, e.g. 10, 20, 30).
 */
export function register(el, order) {
  const toolbar = getToolbar();
  // Prevent duplicate registration
  if (toolbar.items.some(item => item.el === el)) return;
  toolbar.items.push({ el, order });
  reflow();
}

/**
 * Remove a button from the toolbar and reflow remaining items.
 *
 * @param {HTMLElement} el - The element to unregister.
 */
export function unregister(el) {
  const toolbar = getToolbar();
  toolbar.items = toolbar.items.filter(item => item.el !== el);
  reflow();
}
